import type {Thread, Comment} from '@github-ui/conversations'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {
  parseJSONWithBetterErrors,
  throwErrorsIfBadResponse,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'
import {useMutation, useQueryClient} from '@github-ui/react-query'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useDiffSummariesActions, useMarkersActions} from '../stores/selectors'
import {threadPreviewsQueryKey} from '../page-data/payloads/thread-previews'
import {pendingReviewQueryKey, type PendingReviewIDs} from '../page-data/payloads/pending-review'
import {produce} from 'immer'

export type Side = 'left' | 'right'
type MutationData = {
  text: string
  line?: number
  path: string
  side?: Side
  startLine?: number
  startSide?: Side
  subjectType?: string
  submitBatch?: boolean
  comparisonStartOid?: string | null | undefined
  comparisonEndOid?: string | null | undefined
}

type MutationResponse = {
  thread: Thread
  comment: Comment
  totalCommentsCount: number
}

export function useCreateReviewCommentMutation(pullRequestPathName: string) {
  const queryClient = useQueryClient()
  const apiUrl = `${pullRequestPathName}/page_data/${PageData.createReviewComment}`
  const {updateThread} = useMarkersActions()
  const {onCommentThreadAdded} = useDiffSummariesActions()

  return useMutation({
    mutationFn: async (data: MutationData) => {
      const result = await reactFetchJSON(`${apiUrl}`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
        body: data,
      })

      const json = await parseJSONWithBetterErrors(result)
      throwErrorsIfBadResponse(result, json)
      return json
    },
    onSuccess: (data: MutationResponse, inputs: MutationData) => {
      // update the MarkersMap for the TOC store
      const diffLineKey =
        inputs.subjectType === 'file' ? 'FILE' : inputs.side === 'right' ? `R${inputs.line}` : `L${inputs.line}`
      const startPosition =
        inputs.startSide && inputs.startLine
          ? inputs.startSide === 'right'
            ? `R${inputs.startLine}`
            : `L${inputs.startLine}`
          : undefined

      if (inputs.submitBatch !== undefined && !inputs.submitBatch && data.thread) {
        queryClient.setQueryData<PendingReviewIDs>(
          pendingReviewQueryKey(pullRequestPathName),
          produce((oldData: PendingReviewIDs | undefined) => {
            if (!oldData) return oldData
            oldData.pendingReviewIDs ||= []
            oldData.pendingReviewIDs.push(Number(data.thread.id))
          }),
        )
      }

      // Clear the pending review IDs if the comment is submitted as part of a batch
      if (inputs.submitBatch) {
        queryClient.setQueryData<PendingReviewIDs>(
          pendingReviewQueryKey(pullRequestPathName),
          produce((oldData: PendingReviewIDs | undefined) => {
            if (!oldData) return oldData
            oldData.id = undefined
            oldData.pendingReviewIDs = []
            oldData.comments = []
          }),
        )
      }

      // update the Markers Store
      updateThread(Number(data.thread.id), () => data.thread)

      onCommentThreadAdded({
        path: inputs.path,
        diffLineKey,
        startPosition,
        threadID: data.thread.id,
      })

      // invalidate pullRequestFilesToolbar queries
      queryClient.invalidateQueries({
        queryKey: threadPreviewsQueryKey(pullRequestPathName),
      })
    },
  })
}
